import React, { useState } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AuthModal({ isOpen, onClose, asVendor = false }) {
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [devOtp, setDevOtp] = useState("");
  const { login } = useAuth();

  if (!isOpen) return null;

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/send-otp`, { phone });
      setOtpSent(true);
      if (response.data.dev_mode) {
        setDevOtp(response.data.otp);
        toast.success(`OTP sent! Dev Mode: ${response.data.otp}`);
      } else {
        toast.success("OTP sent to your phone");
      }
    } catch (error) {
      toast.error("Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/verify-otp`, {
        phone,
        otp,
        ...(asVendor ? { role: "vendor" } : {}),
      });
      login(response.data.access_token, response.data.user);
      toast.success(asVendor ? "Welcome aboard, vendor!" : "Logged in successfully!");
      
      // Reset modal state
      setPhone("");
      setOtp("");
      setOtpSent(false);
      setDevOtp("");
      
      onClose();
    } catch (error) {
      toast.error("Invalid OTP");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="auth-modal">
      <div className="bg-card rounded-2xl shadow-hover max-w-md w-full mx-4 p-8 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
          data-testid="auth-modal-close-btn"
        >
          <X className="w-6 h-6" />
        </button>

        <h2 className="font-heading text-3xl font-bold text-primary mb-2" data-testid="auth-modal-title">
          {otpSent ? "Verify OTP" : asVendor ? "Register as Vendor" : "Login / Sign Up"}
        </h2>
        <p className="text-muted-foreground mb-6">
          {otpSent
            ? "Enter the OTP sent to your phone"
            : asVendor
            ? "Enter your phone number to create your vendor account"
            : "Enter your phone number to continue"}
        </p>

        {!otpSent ? (
          <form onSubmit={handleSendOtp} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Phone Number</label>
              <Input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+91 9876543210"
                required
                className="w-full"
                data-testid="auth-phone-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-full py-6 text-lg font-medium"
              data-testid="auth-send-otp-btn"
            >
              {loading ? "Sending..." : "Send OTP"}
            </Button>
          </form>
        ) : (
          <form onSubmit={handleVerifyOtp} className="space-y-4">
            {devOtp && (
              <div className="bg-accent/10 border border-accent rounded-xl p-4 mb-4">
                <p className="text-sm font-medium text-accent">Dev Mode - OTP: {devOtp}</p>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Enter OTP</label>
              <Input
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                placeholder="123456"
                required
                maxLength={6}
                className="w-full text-center text-2xl tracking-widest"
                data-testid="auth-otp-input"
              />
            </div>
            <div className="flex space-x-4">
              <Button
                type="button"
                onClick={() => {
                  setOtpSent(false);
                  setOtp("");
                  setDevOtp("");
                }}
                variant="outline"
                className="flex-1 rounded-full"
                data-testid="auth-back-btn"
              >
                Back
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 rounded-full"
                data-testid="auth-verify-otp-btn"
              >
                {loading ? "Verifying..." : "Verify"}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
